import React, { useContext, useEffect, useState } from 'react';
import axios from "axios";
import { navigate } from "raviger";
import { Form, Formik, useFormikContext } from 'formik';
import {
  Button,
  ButtonGroup,
  Card,
  Col,
  FormGroup,
  Modal,
  OverlayTrigger,
  Row,
  Tooltip
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowAltCircleLeft,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import * as Yup from 'yup';
import dayjs from 'dayjs';
import { DropDown, TextInput, DateTimePicker } from '../components/Form';
import { SystemErrorContext } from '../components/SystemError';
import { AuthContext } from "../accounts/AccountsReducer";
import Patient from './components/Patient';

// Child component to handle the useEffect logic
const AutoSubmitEffect = (props) => {
  const { values, submitForm } = useFormikContext();

  useEffect(() => {
    // Add your condition to trigger submission
    if (props.submit_form) {
      submitForm();
    }
  }, [props.submit_form, submitForm]);

  return null;
};

const DiagnosticResultForm = (props) => {

  const { state } = useContext(AuthContext);

  const { setShowSystemError } = useContext(SystemErrorContext);

  const [data, setData] = useState({
    open: new Date(),
    complete: null,
    name: '',
    other_name: '',
    notes: '',
    result: '',
    animal_object: {id:''},
    medical_record: null,
  })

  const [assigneeChoices, setAssigneeChoices] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const cancelDiagnosticOrder = () => {
    axios.delete('/vet/api/diagnosticresults/' + props.id + '/')
    .catch(error => {
      setShowSystemError(true);
    });
    navigate("/" + props.organization + "/" + props.incident + "/vet/medrecord/" + data.medical_record)
  }

  useEffect(() => {
    let unmounted = false;
    let source = axios.CancelToken.source();

    const fetchDiagnosticResult = async () => {
      // Fetch diagnostic result data.
      await axios.get('/vet/api/diagnosticresults/' + props.id + '/?incident=' + state.incident.id, {
        cancelToken: source.token,
      })
      .then(response => {
        if (!unmounted) {
          setData(response.data);
        }
      })
      .catch(error => {
        if (axios.isCancel(error)) {
          console.log('Request canceled gracefully:', error.message);
        } else if (error.response)
        {
          setShowSystemError(true);
        }
      });
    };
    fetchDiagnosticResult();

    const fetchAssignees = async () => {
      // Fetch assignee data.
      await axios.get('/accounts/api/user/?vet_techs=true&organization=' + state.organization.id, {
        cancelToken: source.token,
      })
      .then(response => {
        if (!unmounted) {
          let options = [];
          response.data.forEach(function(person) {
            options.unshift({value: person.id, label: person.first_name + ' ' + person.last_name})
          });
          setAssigneeChoices(options);
        }
      })
      .catch(error => {
        if (axios.isCancel(error)) {
          console.log('Request canceled gracefully:', error.message);
        } else if (error.response)
        {
          setShowSystemError(true);
        }
      });
    };
    fetchAssignees();

    // Cleanup.
    return () => {
      unmounted = true;
      source.cancel();
    };
  }, [props.id]);

  return (
    <>
    <Formik
      initialValues={data}
      enableReinitialize={true}
      validationSchema={Yup.object({
        open: Yup.string(),
        performer: Yup.string().nullable(),
        completed: Yup.string().nullable(),
        result: Yup.string().nullable(),
        other_name: Yup.string().nullable().max(50, 'Maximum character limit of 50.'),
        notes: Yup.string().nullable().max(300, 'Maximum character limit of 300.'),
      })}
      onSubmit={(values, { setSubmitting }) => {
        axios.patch('/vet/api/diagnosticresults/' + props.id + '/', values)
        .then(response => {
          if (props.modal && props.setParentData) {
            props.setParentData(prevItems =>
              prevItems.map(item => {
                if (Number(item.object_id) === Number(props.id)) {
                  // Return a new object with updated properties
                  return { ...item, itemProps: {
                    style: {
                      background: response.data.result ? '#008000' : dayjs(response.data.open).isBefore(dayjs()) ? '#ff4c4c' : '#2196f3',  // Background color
                      color: 'white',       // Text color
                      borderColor: 'black'
                    }
                  }};
                }
                // Return unchanged items as they are
                return item;
              })
            );
            props.setResetSubmit(false);
            props.setShowModal(false);
          }
          else {
            navigate('/' + props.organization + '/' + props.incident + '/vet/medrecord/' + data.medical_record);
          }
        })
        .catch(error => {
          setShowSystemError(true);
        });
        setSubmitting(false);
      }}
    >
      {formikProps => (
        <Card border="secondary" className={props.modal ? "" : "mt-3"}>
          {!props.modal ? <Card.Header as="h5" className="ps-3 pe-3" style={{textTransform:"capitalize"}}>
            {state.prevLocation ?
              <span style={{ cursor: 'pointer' }} onClick={() => navigate(state.prevLocation)} className="me-3"><FontAwesomeIcon icon={faArrowAltCircleLeft} size="lg" inverse /></span>
            :
              <span style={{ cursor: 'pointer' }} onClick={() => navigate('/' + props.organization + "/" + props.incident + "/vet/medrecord/" + data.medical_record)} className="me-3"><FontAwesomeIcon icon={faArrowAltCircleLeft} size="lg" inverse /></span>
            }
            {data.other_name ? data.other_name : data.name} Results
            <OverlayTrigger
              key={"cancel-diagnostic-order"}
              placement="bottom"
              overlay={
                <Tooltip id={`tooltip-cancel-diagnostic-order`}>
                  Cancel diagnostic order
                </Tooltip>
              }
            >
              <FontAwesomeIcon icon={faTimes} className="ms-2 float-end" size="lg" style={{cursor:'pointer'}} inverse onClick={() => {setShowModal(true)}}/>
            </OverlayTrigger>
          </Card.Header> : ""}
          {!props.modal ? <Patient animal={data.animal_object} organization={props.organization} incident={props.incident} /> : ""}
          <Card.Body>
            <Form>
              <FormGroup>
                {data.other_name ?
                <Row className="mb-3">
                  <TextInput
                    type="text"
                    label="Other Diagnostic"
                    name="other_name"
                    id="other_name"
                    xs="6"
                  />
                </Row>
                : ""}
                <Row className="gx-2">
                  <DateTimePicker
                    label="Scheduled"
                    name="open"
                    id="open"
                    xs={props.modal ? "6" : "4"}
                    onChange={(date, dateStr) => {
                      formikProps.setFieldValue("open", dateStr)
                    }}
                    data-enable-time={true}
                    clearable={false}
                    value={formikProps.values.open||new Date()}
                    setFieldValue={formikProps.setFieldValue}
                  />
                  <Col xs={props.modal ? "6" : "4"}>
                    <DropDown
                      label="Performer"
                      id="performerDropdown"
                      name="performer"
                      type="text"
                      key={`my_unique_performer_select_key__${formikProps.values.performer}`}
                      options={assigneeChoices}
                      isClearable={true}
                      onChange={(instance) => {
                        formikProps.setFieldValue("performer", instance === null ? '' : instance.value);
                      }}
                    />
                  </Col>
                </Row>
                <Row className="mt-3 gx-2">
                  <Col xs={props.modal ? "6" : "4"}>
                    <DropDown
                      label="Result"
                      id="resultDropdown"
                      name="result"
                      type="text"
                      options={[{'value':'Normal', 'label':'Normal'}, {'value':'Abnormal', 'label':'Abnormal'}]}
                      value={[{'value':'Normal', 'label':'Normal'}, {'value':'Abnormal', 'label':'Abnormal'}].filter(choice => choice.value === formikProps.values.result)}
                      isClearable={true}
                    />
                  </Col>
                </Row>
                <Row className="mt-3 gx-2">
                  <TextInput
                    as="textarea"
                    label="Notes"
                    name="notes"
                    id="notes"
                    xs={props.modal ? "12" : "8"}
                    rows={3}
                    value={formikProps.values.notes || ''}
                  />
                </Row>
              </FormGroup>
            </Form>
          </Card.Body>
          {!props.modal ? <ButtonGroup>
            <Button type="button" className="btn btn-primary" onClick={() => { formikProps.submitForm()}}>Save</Button>
          </ButtonGroup> : ""}
          <AutoSubmitEffect submit_form={props.submit_form} />
        </Card>
      )}
    </Formik>
    <Modal show={showModal} onHide={() => setShowModal(false)}>
      <Modal.Header closeButton>
        <Modal.Title>Confirm Diagnostic Order Cancelation</Modal.Title>
      </Modal.Header>
      <Modal.Body>Are you sure you want to cancel this diagnostic order?</Modal.Body>
      <Modal.Footer>
        <Button variant="primary" onClick={() => cancelDiagnosticOrder()}>
          Yes
        </Button>
        <Button variant="secondary" onClick={() => setShowModal(false)}>
          Cancel
        </Button>
      </Modal.Footer>
    </Modal>
    </>
  );
};

export default DiagnosticResultForm;
